import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Container, Dropdown, DropdownButton, Form, Modal, Row, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { PencilIcon } from '@primer/octicons-react';
import { BiCalendar, BiHelpCircle, BiXCircle } from "react-icons/bi";
import DateTimePicker from 'react-datetime-picker';
import { axiosAuth_IDENT_MGMT_API } from '../utils/AxoisUtilComponent';
import { axiosAuth_PLATFORM_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import { UserContext } from "../UserContext";
import classes from './AdminPages.module.css';
import configInfo from '../config/config.json';
import { translateAPIError } from '../utils/apiUtils';
import { formatDate } from '../utils/timeUtils';
import { LoadingSpinner, validateEmail, hasAnyPerm } from '../utils/uiUtils';


const UserAdminDetailsView = (props) => {

  // Utilities
  const { i18n, t } = useTranslation(['common', 'custom']);

  const { user } = useContext(UserContext);
  const navigate = useNavigate();


  const [isSysAdminUser, setIsSysAdminUser] = useState(false);

  const [errMsg, setErrMsg] = useState('');

  // Initialize with actual JSON obj.  If not, will get error.
  const [userAttrs, setUserAttrs] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    userType: '',
    accountType: '',
    email: '',
    surname: '',
    givenName: '',
    middleName: '',
    fullNameLang2: '',
    fullNameLang3: '',
    mobilePhone: '',
    workPhone: '',
    jobTitle: '',
    deptName: '',
    deptNameLang2: '',
    deptNameLang3: '',
    empId: '',
    mfaWithEmail: '',
    mfaWithSMS: '',
    activeStartTime: '',
    activeExpireTime: '',
    status: '',
    creatorUsername: '',
    timeCreated: '',
    updaterUsername: '',
    timeUpdated: '',
    timePwdUpdated: ''
  });


  const [ugroupList, setUgroupList] = useState([]);

  //const [roleList, setRoleList] = useState([]);
  const [, setRoleList] = useState([]);             // ##### Temp quick fix to silence the elint #####
  const [rolePermList] = useState([]);

  const [showRoleInfo, setShowRoleInfo] = useState(false);
  const [roleSelected, setRoleSelected] = useState();

  const [roleAddList, setRoleAddList] = useState([]);

  const [unassignedRoleList] = useState();

  const [showAddRoleDialog, setShowAddRoleDialog] = useState(false);

  const [activeStartTime,  setActiveStartTime]  = useState(null);
  const [activeExpireTime, setActiveExpireTime] = useState(null);


  const fetchUserAttrs = (user) => {

    setUserAttrs(user);

    setNewUserType({
      key:   user.userType,
      value: user.userType
    });

    setNewUserStatus({
      key:   user.status,   // Reserved for future when key and value may differ
      value: user.status
    });

    setNewDept({
      id:       user.deptId,
      nameCode: user.deptNameCode
    });

    setNewLng({
      key:   user.language,
      value: user.language
    });

    setNewAccountType({
      key:   user.accountType,
      value: user.accountType
    });

    setNewGeneralInfo({
      jobTitle: user.jobTitle ? user.jobTitle : '',
      empId:    user.empId    ? user.empId    : ''
    });

    setNewName({
      surname: user.surname ? user.surname : '',
      givenName: user.givenName ? user.givenName : '',
      middleName: user.middleName ? user.middleName : '',
      fullNameLang2: user.fullNameLang2 ? user.fullNameLang2 : ''
    });

    setNewContactInfo({
      email: user.email ? user.email : '',
      mobilePhone: user.mobilePhone ? user.mobilePhone : '',
      workPhone: user.workPhone ? user.workPhone : ''
    });

    setNewMfaWithEmail(user.mfaWithEmail);
    setNewMfaWithSMS  (user.mfaWithSMS);

    if (user.activeStartTime) {
      setActiveStartTime(new Date(user.activeStartTime));
    }

    if (user.activeExpireTime) {
      setActiveExpireTime(new Date(user.activeExpireTime));
    }
  }

  const [platformConfig, setPlatformConfig] = useState([]);
  const [deptList,       setDeptList]       = useState([]);

  function fetchPlatformConfig() {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_PLATFORM_API.get("platform-config");

        resolve(res.data);

        if (res.status === 200) {
          setPlatformConfig(res.data[0]);
        }
        setErrMsg('');
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });
  }

  const fetchDepts = () => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_IDENT_MGMT_API.get("departments");

        resolve(res.data);

        if (res.status === 200) {

          for (let i=0; i<res.data.length; i++) {
            res.data[i].displayName = [];
            res.data[i].displayName['en']    = res.data[i].name;
            res.data[i].displayName['zh_HK'] = res.data[i].nameLang2;
            res.data[i].displayName['zh_CN'] = res.data[i].nameLang3;
          }

          setDeptList(res.data);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }

  // Update user info, all updatable fields, except security related settings
  const updateUserAttrs = (newUserAttrs, setErrMsg) => {

    const reqBody = newUserAttrs;

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_IDENT_MGMT_API.put("users/" + newUserAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {

          // Restore UI to viewing mode
          setEditModeGeneralInfo(false);
          setEditModeName(false);
          setEditModeContactInfo(false);

          // Refresh data to update Time Updated stamp, and in case there are other stale fields
          refreshUserAttrs(props.objDetails.id);
        }

        setErrMsg('');
      } catch (error) {
        if (error && error.response && error.response.data && (error.response.data.msg === 'ERR_Dup_unique_field')) {
          setErrMsg(t("I18N_ERR_New_user_dup"));
        } else {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });
  }

  const refreshUserAttrs = (userId) => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_IDENT_MGMT_API.get("users/" + userId);

        resolve(res.data);

        if (res.status === 200) {

          fetchUserAttrs(res.data[0]);
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });
  }


  // Update MFA settings
  const updateMfa = (newUserAttrs) => {

    const reqBody = {
      mfaWithEmail: newUserAttrs.mfaWithEmail,
      mfaWithSMS:   newUserAttrs.mfaWithSMS,
      password:     ''                       // Setting my admin does not require pwd verification
    }

    return new Promise(async (resolve, reject) => {
      setLoading(true); // For loading spinner

      try {
        const res = await axiosAuth_IDENT_MGMT_API.put("mfa/" + newUserAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {
          setLoading(false);
          // Restore UI to viewing mode
          setEditModeMFA(false);
        }

        setNewMfaErrMsg('');
      } catch (error) {
        setNewMfaErrMsg(t(translateAPIError(error)));
      }
    });
  }

  // UI Functions

  // Initialize state
  useEffect(() => {

    setIsSysAdminUser(user && user.userInfo && (user.userInfo.username === 'SYSTEM'));

    fetchDepts();
    fetchPlatformConfig();

    if (props) {
      fetchUserAttrs(props.objDetails);

      fetchAssociatedUgroups(props.objDetails.id);
      fetchAssociatedRoles(props.objDetails.id);
    }

    if (hasAnyPerm(user, ["UserAdminDeactivateAccesss"], true)) {
      setStatusList([
        { key: "USR_ST_ACTIVE",      value: "USR_ST_ACTIVE"      },
        { key: "USR_ST_SUSPENDED",   value: "USR_ST_SUSPENDED"   },
        { key: "USR_ST_DEACTIVATED", value: "USR_ST_DEACTIVATED" }]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const fetchAssociatedUgroups = (userId) => {

    if (!userId) {
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_IDENT_MGMT_API.get("users/" + userId + "/usergroups");

        resolve(res.data);

        if (res.status === 200) {
          setUgroupList(res.data);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }


  const fetchAssociatedRoles = (userId) => {

    if (!userId) {
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_IDENT_MGMT_API.get("users/" + userId + "/roles");

        resolve(res.data);

        if (res.status === 200) {
          setRoleList(res.data);
        } else {
          setRoleList([]);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }

  // const fetchRolePermisions = (roleId) => {

  //   if (!roleId) {
  //     return;
  //   }

  //   return new Promise(async (resolve, reject) => {
  //     try {
  //       const res = await axiosAuth_IDENT_MGMT_API.get("roles/" + roleId + "/permissions");

  //       resolve(res.data);

  //       if (res.status === 200) {
  //         setRolePermList(res.data);
  //       }

  //       setErrMsg('');
  //     } catch (error) {
  //       setErrMsg(t(translateAPIError(error)));
  //     }
  //   });
  // }

  // const fetchUnassignedRoles = () => {

  //   return new Promise(async (resolve, reject) => {
  //     try {
  //       const res = await axiosAuth_IDENT_MGMT_API.get("roles");

  //       resolve(res.data);

  //       if (res.status === 200) {
  //         // Remove roles already assigned to the user
  //         let roleIdsArray = [];

  //         for (let i=0; i<roleList.length; i++) {
  //           roleIdsArray.push(roleList[i].id);
  //         }

  //         let unassignedRolesArray = [];
  //         for (let i=0; i<res.data.length; i++) {
  //           if (!roleIdsArray.includes(res.data[i].id)) {
  //             unassignedRolesArray.push(res.data[i]);
  //           }
  //         }

  //         setUnassignedRoleList(unassignedRolesArray);
  //       }

  //       setErrMsg(''); //### NEED TO FIX
  //     } catch (error) {
  //       setErrMsg(t(translateAPIError(error)));  //### NEED TO FIX
  //     }
  //   });
  // }

  // View vs Edit Modes
  const [editModeGeneralInfo,   setEditModeGeneralInfo] = useState(false);
  const [editModeName,          setEditModeName]        = useState(false);
  const [editModeContactInfo,   setEditModeContactInfo] = useState(false);
  const [editModeMFA,           setEditModeMFA]         = useState(false);
  const [editModePwdInfo,       setEditModePwdInfo]     = useState(false);

  const [newGeneralInfo, setNewGeneralInfo] = useState(
    {
      jobTitle: '',
      empId:    ''
    }
  );

  const [newUserType,    setNewUserType]    = useState({});
  const [newUserStatus,  setNewUserStatus]  = useState({});
  const [newDept,        setNewDept]        = useState({});
  const [newLng,         setNewLng]         = useState({});
  const [newAccountType, setNewAccountType] = useState({});

  const [newName, setNewName] = useState(
    {
      surname: '',
      givenName: '',
      middleName: '',
      fullNameLang2: ''
    }
  );

  const [newContactInfo, setNewContactInfo] = useState(
    {
      email: '',
      mobilePhone: '',
      workPhone: ''
    }
  );

  const [newPwdInfo, setNewPwdInfo] = useState(
    {
      password: '',
      confirmPassword: ''
    }
  );

  const [newMfaWithEmail, setNewMfaWithEmail] = useState(false);
  const [newMfaWithSMS,   setNewMfaWithSMS]   = useState(false);

  let { empId, jobTitle } = newGeneralInfo;
  let { surname, givenName, middleName, fullNameLang2 } = newName;
  let { email, mobilePhone, workPhone } = newContactInfo;
  let { password, confirmPassword } = newPwdInfo;

  const [newGeneralInfoErrMsg, setNewGeneralInfoErrMsg] = useState('');
  const [newNameErrMsg, setNewNameErrMsg] = useState('');
  const [newContactInfoErrMsg, setNewContactInfoErrMsg] = useState('');
  const [newMfaErrMsg, setNewMfaErrMsg] = useState('');
  const [pwdResetMsg, setPwdResetMsg] = useState('');
  const [pwdResetErrMsg, setPwdResetErrMsg] = useState('');
  //const [updateUserErrMsg, setUpdateUserErrMsg] = useState('');
  const [addRoleErrMsg, setAddRoleErrMsg] = useState('');
  const [newPwdInfoErrMsg, setNewPwdInfoErrMsg] = useState('');

  // ##### TODO: Need to create a DB table for this #####
  const userTypeList = [
    { key: "UTYPE_ADMIN", value: "UTYPE_ADMIN" },
    { key: "UTYPE_USER",  value: "UTYPE_USER" },
    { key: "UTYPE_APP",   value: "UTYPE_APP" }
  ];

  const [statusList, setStatusList] = useState([
    { key: "USR_ST_ACTIVE",    value: "USR_ST_ACTIVE" },
    { key: "USR_ST_SUSPENDED", value: "USR_ST_SUSPENDED" }
  ]);
/*
  let statusList = [
    { key: "USR_ST_ACTIVE",    value: "USR_ST_ACTIVE" },
    { key: "USR_ST_SUSPENDED", value: "USR_ST_SUSPENDED" }
*/
/*
    { key: "USR_ST_DISABLED",  value: "USR_ST_DISABLED" },
    { key: "USR_ST_PEND_ACTV", value: "USR_ST_PEND_ACTV" },
    { key: "USR_ST_LOCKED",    value: "USR_ST_LOCKED" }
*/
/*
  ];
*/

  const lngList = [
    { key: "en",    value: "en" },
    { key: "zh_CN", value: "zh_CN" },
    { key: "zh_HK", value: "zh_HK" }
  ];

  const accountTypeList = [
    ...(configInfo.appConfig.isPrimaryIdentProvider ?
   [{ key: "USER",      value: "USER" }] : []),
    { key: "SYS_ADMIN", value: "SYS_ADMIN" },
    { key: "API",       value: "API" }
  ];

  const [loading, setLoading] = useState(false);

  const handleEditGeneralInfo = (e) => {
    e.preventDefault();
    setEditModeGeneralInfo(true);

    // Close other edit areas to avoid cluttering
    handleEditNameCancel(e);
    handleEditContactInfoCancel(e);
    handleEditMFACancel(e);
    handleEditPwdInfoCancel(e);

    setErrMsg("");
  }

  const handleEditName = (e) => {
    e.preventDefault();
    setEditModeName(true);

    // Close other edit areas to avoid cluttering
    handleEditGeneralInfoCancel(e);
    handleEditContactInfoCancel(e);
    handleEditMFACancel(e);
    handleEditPwdInfoCancel(e);

    setErrMsg("");
  }

  const handleEditContactInfo = (e) => {
    e.preventDefault();
    setEditModeContactInfo(true);

    // Close other edit areas to avoid cluttering
    handleEditGeneralInfoCancel(e);
    handleEditNameCancel(e);
    handleEditMFACancel(e);
    handleEditPwdInfoCancel(e);

    setErrMsg("");
  }

  const handleEditMFA = (e) => {
    e.preventDefault();
    setEditModeMFA(true);

    // Close other edit areas to avoid cluttering
    handleEditGeneralInfoCancel(e);
    handleEditNameCancel(e);
    handleEditContactInfoCancel(e);
    handleEditPwdInfoCancel(e);

    setErrMsg("");
  }

  const handleEditPwdInfo = (e) => {
    e.preventDefault();
    setEditModePwdInfo(true);

    // Close other edit areas to avoid cluttering
    handleEditGeneralInfoCancel(e);
    handleEditContactInfoCancel(e);
    handleEditNameCancel(e);
    handleEditMFACancel(e);

    setErrMsg("");
  }

  // function handleAddRoles() {
  //   fetchUnassignedRoles();
  //   setShowAddRoleDialog(true);
  // }

  function handleAddRolesClose() {
    setRoleAddList([]);
    setShowAddRoleDialog(false);
  }

  function handleAddRolesSubmit() {
    let roleIds = [];

    if (roleAddList.length === 0) {
      setShowAddRoleDialog(false);
      return;
    }

    for (let i=0; i<roleAddList.length; i++) {
      roleIds.push(parseInt(roleAddList[i]));
    }

    let reqBody = {
          roleIds: roleIds
        };

    return new Promise(async (resolve, reject) => {
      try {

        const res = await axiosAuth_IDENT_MGMT_API.post("users/" + userAttrs.id + "/roles", reqBody);

        resolve(res.data);

        if (res.status === 200) {
          setRoleAddList([]);
          setAddRoleErrMsg('');
          setShowAddRoleDialog(false);
          fetchAssociatedRoles(userAttrs.id);
        } else {
          // ##### NEED TO DECIDE WHAT TO DO #####
        }
      } catch (error) {
        setAddRoleErrMsg(t(translateAPIError(error)));
      }
    });
  }


  const onGeneralInfoInputChange = (e) => {
    setNewGeneralInfo({ ...newGeneralInfo, [e.target.name]: e.target.value })
  }

/*
  const onUserTypeSelect = (key, event) => {
    setNewUserType({key: key, value: key});
  };
*/

  const onUserStatusSelect = (key, event) => {
    setNewUserStatus({key: key, value: key});
  };

/*
  const onDeptSelect = (key, event) => {
    let dept = deptList.find(item => item.id.toString() === key);  // Use == here since the id from DB is int, and key is string.

    setNewDept({id: key, nameCode: dept.nameCode});

    userAttrs.deptNameCode = dept.nameCode;
    setUserAttrs(userAttrs);
  };
*/

  const onDeptClick = (deptId) => {
    setNewDept({id: deptId});
  };

  const onLngSelect = (key, event) => {
    setNewLng({key: key, value: key});
  };

  const onAccountTypeSelect = (key, event) => {
    setNewAccountType({key: key, value: key});
  };

  const onNameInputChange = (e) => {
    setNewName({ ...newName, [e.target.name]: e.target.value })
  }

  const onContactInfoInputChange = (e) => {
    setNewContactInfo({ ...newContactInfo, [e.target.name]: e.target.value })
  }

  const onPwdInfoInputChange = (e) => {
    setNewPwdInfo({ ...newPwdInfo, [e.target.name]: e.target.value })
  }


  const onDatepickerChangeStart = (value) => {
    let newTime = (new Date(value)).getTime();
    let currentTime = (new Date()).getTime();

    // ### This check is not necessary, since the UI already built guardrail.  But keep it here for now. ###
    if (!value && (value != null) && (newTime < currentTime)) {
      setErrMsg(t("I18N_ERR_Date_must_be_future"));
    } else {
      setErrMsg(t(""));
      setActiveStartTime(value);
    }
  }

  const onDatepickerChangeUntil = (value) => {
    let newTime = (new Date(value)).getTime();
    let currentTime = (new Date()).getTime();

    // ### This check is not necessary, since the UI already built guardrail.  But keep it here for now. ###
    if (!value && (value != null) && (newTime < currentTime)) {
      setErrMsg(t("I18N_ERR_Date_must_be_future"));
    } else {
      setErrMsg(t(""));
      setActiveExpireTime(value);
    }
  }

  // --- Edit General Info ---

  const handleEditGeneralInfoCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setNewGeneralInfo({
      jobTitle: userAttrs.jobTitle ? userAttrs.jobTitle : '',
      empId:    userAttrs.empId    ? userAttrs.empId    : ''
    });

    setNewUserType(
      userAttrs.userType ?
      {
        key:   userAttrs.userType ? userAttrs.userType : '',
        value: userAttrs.userType ? userAttrs.userType : '',
      }
      :
      userTypeList[0]
    );

    setNewUserStatus(
      userAttrs.status ?
      {
        key:   userAttrs.status ? userAttrs.status : '',
        value: userAttrs.status ? userAttrs.status : '',
      }
      :
      statusList[0]
    );

    let dept = (userAttrs.deptId ? deptList.find(item => item.id.toString() === userAttrs.deptId.toString()) : null);

    setNewDept(
      dept ?
      {
        id:       dept.id,
        nameCode: dept.nameCode
      }
      :
      {
        id:       deptList[0].id,
        nameCode: deptList[0].nameCode
      }
    );

    setNewLng(
      userAttrs.language ?
      {
        key:   userAttrs.language ? userAttrs.language : 'en',
        value: userAttrs.language ? userAttrs.language : 'en',
      }
      :
      lngList[0]
    );

    if (userAttrs.activeStartTime) {
      setActiveStartTime(new Date(userAttrs.activeStartTime));
    }

    if (userAttrs.activeExpireTime) {
      setActiveExpireTime(new Date(userAttrs.activeExpireTime));
    }

    setNewGeneralInfoErrMsg("");
    setEditModeGeneralInfo(false);
  }

  const handleEditGeneralInfoSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (newGeneralInfo.jobTitle) {
      let regex = /^[a-zA-Z0-9-_.@ ]{2,80}$/;
      if (!regex.test(newGeneralInfo.jobTitle)) {
        setNewGeneralInfoErrMsg(t("I18N_ERR_Valid_job_title"));
        return;
      }
    }

    if (newGeneralInfo.empId) {
      let regex = /^[a-zA-Z0-9-_.]{2,20}$/;
      if (!regex.test(newGeneralInfo.empId)) {
        setNewGeneralInfoErrMsg(t("I18N_ERR_Valid_emp_id"));
        return;
      }
    }

    setNewGeneralInfoErrMsg('');

    let newUserAttrs = userAttrs;

    newUserAttrs.jobTitle    = newGeneralInfo.jobTitle;
    newUserAttrs.empId       = newGeneralInfo.empId;
    newUserAttrs.userType    = newUserType.key;
    newUserAttrs.status      = newUserStatus.key;
    newUserAttrs.language    = newLng.key;
    newUserAttrs.accountType = isSysAdminUser ? newAccountType.key : 'USER';  // Only sys admin can set account type

    if (newDept.id) {
      let dept = deptList.find(item => item.id.toString() === newDept.id.toString());
      newUserAttrs.deptId        = dept.id;
      newUserAttrs.deptName      = dept.name;
      newUserAttrs.deptNameLang2 = dept.nameLang2;
      newUserAttrs.deptNameLang3 = dept.nameLang3;
    }

    newUserAttrs.activeStartTime  = (activeStartTime  ? activeStartTime.toISOString()  : "");
    newUserAttrs.activeExpireTime = (activeExpireTime ? activeExpireTime.toISOString() : "");

    updateUserAttrs(newUserAttrs, setNewGeneralInfoErrMsg);

    setUserAttrs(newUserAttrs);
  }

  // --- Edit Name Info ---

  const handleEditNameCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setNewName({
      surname: userAttrs.surname ? userAttrs.surname : '',
      givenName: userAttrs.givenName ? userAttrs.givenName : '',
      middleName: userAttrs.middleName ? userAttrs.middleName : '',
      fullNameLang2: userAttrs.fullNameLang2 ? userAttrs.fullNameLang2 : ''
    });

    setEditModeName(false);
  }

  const handleEditNameSubmit = (e) => {
    e.preventDefault();

    // Validate fields
    if (newName.surname && (newName.surname.length > 30)) {
      setNewNameErrMsg(t("I18N_ERR_Max_len_surname") + 30);
      return;
    }

    if (newName.givenName && newName.givenName.length > 30) {
      setNewNameErrMsg(t("I18N_ERR_Max_len_given_name") + 30);
      return;
    }

    if (newName.middleName && newName.middleName.length > 30) {
      setNewNameErrMsg(t("I18N_ERR_Max_len_middle_name") + 30);
      return;
    }

    if (newName.fullNameLang2 && newName.fullNameLang2.length > 100) {
      setNewNameErrMsg(t("custom:I18N_ERR_Max_len_full_name_lang2") + 100);
      return;
    }

/*
    if (!newName.surname || newName.surname.length === 0) {
      setNewNameErrMsg(t("I18N_ERR_Need_Surname"));
    } else if (!newName.givenName || newName.givenName.length === 0) {
      setNewNameErrMsg(t("I18N_ERR_Need_Given_name"));
    } else {
*/
      setNewNameErrMsg('');

      let newUserAttrs = userAttrs;

      newUserAttrs.surname = newName.surname;
      newUserAttrs.givenName = newName.givenName;
      newUserAttrs.middleName = newName.middleName;
      newUserAttrs.fullNameLang2 = newName.fullNameLang2;

      updateUserAttrs(newUserAttrs, setNewNameErrMsg);
      setUserAttrs(newUserAttrs);
/*
    }
*/
  }


  // --- Edit Contact Info ---

  const handleEditContactInfoCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setNewContactInfo({
      email: userAttrs.email ? userAttrs.email : '',
      mobilePhone: userAttrs.mobilePhone ? userAttrs.mobilePhone : '',
      workPhone: userAttrs.workPhone ? userAttrs.workPhone : ''
    });

    setEditModeContactInfo(false);
    setNewContactInfoErrMsg('');
  }

  const handleEditContactInfoSubmit = (e) => {
    e.preventDefault();

    if (!newContactInfo.email || newContactInfo.email.length === 0) {
      setNewContactInfoErrMsg(t("I18N_ERR_Need_Email"));
      return;
    } else if (!validateEmail(newContactInfo.email)) {
      setNewContactInfoErrMsg(t("I18N_ERR_Bad_Email"));
      return;
    } else if (newContactInfo.email.length > 60) {
      setNewContactInfoErrMsg(t("I18N_ERR_Max_len_email") + 60);
      return;
    }
/*
    } else if (!newContactInfo.mobilePhone || newContactInfo.mobilePhone.length === 0) {
      setNewContactInfoErrMsg(t("I18N_ERR_Need_Mobile_Phone"));
      return;
    } else {
*/
    if (newContactInfo.mobilePhone && configInfo.uiConfig.validateFields.USER_phone) {
      let regex = /^(?:\+852\s?)?[2-9]\d{7}$/;
      if (!regex.test(newContactInfo.mobilePhone)) {
        setNewContactInfoErrMsg(t("I18N_ERR_Valid_mobile_phone"));
        return;
      }
    }

    if (newContactInfo.workPhone && configInfo.uiConfig.validateFields.USER_phone) {
      let regex = /^(?:\+852\s?)?[2-9]\d{7}$/;
      if (!regex.test(newContactInfo.workPhone)) {
        setNewContactInfoErrMsg(t("I18N_ERR_Valid_work_phone"));
        return;
      }
    }

      setNewContactInfoErrMsg('');

      let newUserAttrs = userAttrs;

      newUserAttrs.email = newContactInfo.email;
      newUserAttrs.mobilePhone = newContactInfo.mobilePhone;
      newUserAttrs.workPhone = newContactInfo.workPhone;

      updateUserAttrs(newUserAttrs, setNewContactInfoErrMsg);
      setUserAttrs(newUserAttrs);
/*
    }
*/
  }

  // --- Edit Password Info ---

  const handleEditPwdInfoCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setNewPwdInfo({
      password: '',
      confiormPassword: ''
    });

    setEditModePwdInfo(false);
    setNewPwdInfoErrMsg('');
  }

  const handleEditPwdInfoSubmit = (e) => {
    e.preventDefault();

    if (!password || password.length === 0) {
      setNewPwdInfoErrMsg(t("I18N_ERR_Need_Password"));
      return;
    } else if (!validatePwdComplexity(password)) {
      setNewPwdInfoErrMsg(t("I18N_ERR_Poor_Password"));
      return;
    } else if (confirmPassword !== password) {
      setNewPwdInfoErrMsg(t("I18N_ERR_Pass_Mismatch"));
      return;
    }

    updateUserPwd();
  }


  const updateUserPwd = () => {

    return new Promise(async (resolve, reject) => {

      setNewPwdInfoErrMsg("");

      try {
        const reqBody = {
                password: password
              };

        const res = await axiosAuth_IDENT_MGMT_API.put("password/" + userAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {

          // Reset fields
          setNewPwdInfo({
            password: '',
            confiormPassword: ''
          });

          setEditModePwdInfo(false);
          setNewPwdInfoErrMsg('');

          // ##### Future enhancement: Provide feedback for successful pwd update #####
        }

        setNewPwdInfoErrMsg('');
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setNewPwdInfoErrMsg(t(translateAPIError(error)));
        }
      }
    });
  }


  const resetUserPwd = () => {

    return new Promise(async (resolve, reject) => {
      setLoading(true);

      setErrMsg("");

      try {
        const res = await axiosAuth_IDENT_MGMT_API.put("password/" + userAttrs.id);

        resolve(res.data);

        if (res.status === 200) {
          setPwdResetMsg(t("I18N_Pwd_reset_done"));
          setLoading(false);

          // This to refresh the status of the user
          refreshUserAttrs(props.objDetails.id);
        }

        setPwdResetErrMsg('');
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setPwdResetErrMsg(t(translateAPIError(error)));
          setPwdResetMsg('');

          setLoading(false);
        }
      }
    });
  }


  const handleEditMFACancel = (e) => {
    e.preventDefault();

    setNewMfaWithEmail(userAttrs.mfaWithEmail ? userAttrs.mfaWithEmail : false);
    setNewMfaWithSMS(userAttrs.mfaWithSMS     ? userAttrs.mfaWithSMS   : false);

    setEditModeMFA(false);
  }

  const handleEditMFASubmit = (e) => {
    e.preventDefault();

    let newUserAttrs = userAttrs;

    newUserAttrs.mfaWithEmail = newMfaWithEmail;
    newUserAttrs.mfaWithSMS   = newMfaWithSMS;

    updateMfa(newUserAttrs);
    setUserAttrs(newUserAttrs);
  }

  const handleClose = (e) => {
    e.preventDefault();

    // Return to list
    // props.onUIMode('LIST')
    navigate(-1);
    /*
        navigate("/AdminHome/Dashboard", {
          state: {
            //...values
            target: 'Dashboard'
          }
        });
    */
  }

  const handleReissueActv = (e) => {
    e.preventDefault();

    return new Promise(async (resolve, reject) => {
      setLoading(true);

      try {
        const res = await axiosAuth_IDENT_MGMT_API.put("user-reissue-actv/" + userAttrs.id);

        resolve(res.data);

        if (res.status === 200) {
          setLoading(false);

          // This to refresh the status of the user
          refreshUserAttrs(props.objDetails.id);

          setErrMsg(t("I18N_Reissue_user_actv_done"));
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));

          setLoading(false);
        }
      }
    });
  }

  function renderUgroup(ugroup, index) {
    return (
      <tr key={index} style={{fontWeight:'300'}}>
        <td>{ugroup.name}</td>
        <td>{ugroup.nameLang2}</td>
        <td>{ugroup.nameLang3}</td>
        <td>{formatDate(ugroup.timeCreated)}</td>
      </tr>
    )
  }

  // function handleRoleInfo(role) {
  //   // Fetch permissions associated with the selected role
  //   fetchRolePermisions(role.id);

  //   setRoleSelected(role);
  //   setShowRoleInfo(true);
  // }

  const handleRoleInfoClose = (e) => {
    setRoleSelected(null);
    setShowRoleInfo(false);
  }


  // function handleRemoveRole(role) {

  //   let reqBody = {
  //         data: {
  //            "roleIds": []
  //         }
  //       };

  //   reqBody.data.roleIds.push(role.id);

  //   return new Promise(async (resolve, reject) => {
  //     try {

  //       const res = await axiosAuth_IDENT_MGMT_API.delete("users/" + userAttrs.id + "/roles/", reqBody);

  //       resolve(res.data);

  //       if (res.status === 200) {
  //         setErrMsg('');
  //         fetchAssociatedRoles(userAttrs.id);
  //       } else {
  //         // ##### NEED TO DECIDE WHAT TO DO #####
  //       }
  //     } catch (error) {
  //       setErrMsg(t(translateAPIError(error)));
  //     }
  //   });
  // }

  const handleRoleSelect = (e) => {

    if (e.target.checked) {
      // Add role to the list
      roleAddList.push(e.target.id);
    } else {
      const index = roleAddList.indexOf(e.target.id);
      if (index > -1) {
        roleAddList.splice(index, 1);
      }
    }

    setRoleAddList(roleAddList);
  }


  // function renderRole(role, index) {
  //   return (
  //     <tr key={index} style={{fontWeight:'300'}}>
  //       <td><span style={{cursor:'pointer', color:'SteelBlue'}} onClick={() => handleRoleInfo(role)}>{role.id}</span></td>
  //       <td>{t("custom:I18N_" + role.nameCode)}</td>
  //       <td>{role.description}</td>
  //       { configInfo.appConfig.isPrimaryIdentProvider ?
  //         <td><span style={{cursor:'pointer', color:'SteelBlue'}} onClick={() => handleRemoveRole(role)}><BiTrash size={16} /></span></td>
  //       :
  //         null
  //       }
  //     </tr>
  //   )
  // }


  function renderRolePerm(perm, index) {
    return (
      <tr key={index} style={{fontWeight:'300'}}>
        <td>{perm.nameCode}</td>
        <td>{perm.description}</td>
      </tr>
    )
  }


  function renderAddRoles(role, index) {
    return (
      <tr key={index} style={{fontWeight:'300'}}>
        <td><Form.Check type="checkbox" id={role.id} label="" onChange={handleRoleSelect} /></td>
        <td>{t("custom:I18N_" + role.nameCode)}</td>
        <td>{role.description}</td>
      </tr>
    )
  }


  function validatePwdComplexity(password) {
    // Password policy: Min length, upper case, lower case, special char, etc.
    //let strongPassword = new RegExp('(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+={\}[\]|\\:;"\'<>,.?/]).{12,30}$');
    let strongPassword = new RegExp(decodeURIComponent(platformConfig.config.public.pwdComplexity.testRegExp));

    if (!strongPassword.test(password)) {
      return (false);
    } else {
      return (true);
    }
  }


  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_User_Details")}
        </span>
{/*
        {userAttrs && userAttrs.userType === 'UTYPE_SYSADMIN' ?
        <span className={classes.admin_pages__field_label} style={{marginLeft:'20px', alignSelf: 'center', marginTop:'6px', fontWeight:'400'}}>
          {t("I18N_Quorum_notice")}
        </span>
        : null}
*/}
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_General_info")}
            </span>
            {!configInfo.uiConfig.editableFields.USER_SECTION_General || editModeGeneralInfo || (!configInfo.appConfig.isPrimaryIdentProvider && userAttrs.accountType === 'USER') ?
              null
              :
              <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditGeneralInfo}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
            }
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editModeGeneralInfo ?
            <Container>
              <form>
                <Row className={classes.admin_pages__field_label}>
                  <Col md={3}>{t("I18N_Username")}:</Col>
                  { configInfo.uiConfig.visibleFields.USER_jobTitle ? <Col>{t("I18N_Job_title")}:</Col> : null }
                  { configInfo.uiConfig.visibleFields.USER_dept     ? <Col>{t("I18N_Dept_name")}:</Col> : null }
                  { configInfo.uiConfig.visibleFields.USER_empId    ? <Col>{t("I18N_Emp_id")}:</Col>    : null }
{/*
                  { configInfo.uiConfig.visibleFields.USER_userType ?
                  <Col>{t("I18N_User_type")}: *</Col>
                  : <Col></Col>
                  }
*/}
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col md={3}>{userAttrs.username}</Col>
                  { configInfo.uiConfig.visibleFields.USER_jobTitle ?
                  <Col md={3}>
                    <input className={classes.admin_pages__field_editable} name="jobTitle" value={jobTitle}
                      onChange={(e) => onGeneralInfoInputChange(e)} />
                  </Col>
                  : <Col />
                  }
                  { configInfo.uiConfig.visibleFields.USER_dept ?
                  <Col md={3}>
{/*
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={!newDept.id ? t("I18N_No_dept") :
                             deptList.find(item => item.id.toString() === newDept.id.toString()).displayName[i18n.language]}
                      onSelect={onDeptSelect}>
                      {deptList.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.id} className={classes.dropdown_item_wrap}>
                            {item.deptCode === 'NONE' ? t("I18N_No_dept") : item.displayName[i18n.language]}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
*/}
                    <Dropdown>
                      <Dropdown.Toggle size="tiny_dropdown" variant="outline-secondary" className={classes.dropdown_item_wrap} id="dropdown-basic-button">
                        {!newDept.id ? t("I18N_No_dept") :
                           (deptList.find( ({ id }) => id === parseInt(newDept.id) )).displayName[i18n.language]}
                      </Dropdown.Toggle>

                      <Dropdown.Menu style={{ maxHeight: "300px", overflowY: "auto"}} id="dropdown-basic-button" >
                        {deptList.map((item, index) => {
                          return (
                            <Dropdown.Item key={index} eventKey={item.id} className={classes.dropdown_item_wrap} onClick={() => onDeptClick(item.id)}>
                              {item.displayName[i18n.language]}
                            </Dropdown.Item>
                          );
                        })}
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                  : <Col />
                  }
                  { configInfo.uiConfig.visibleFields.USER_empId  ?
                  <Col md={3}>
                    <input className={classes.admin_pages__field_editable} name="empId" value={empId}
                      onChange={(e) => onGeneralInfoInputChange(e)} />
                  </Col>
                  : <Col />
                  }
{/*
                  { configInfo.uiConfig.visibleFields.USER_userType ?
                  <Col>
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={t("common:I18N_" + newUserType.value)}
                      onSelect={onUserTypeSelect}>
                      {userTypeList.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                            {t("common:I18N_" + item.value)}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </Col>
                  : <Col></Col>
                  }
*/}
                </Row>

                <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  { configInfo.uiConfig.visibleFields.USER_status ?
                  <Col md={6}>{t("I18N_User_status")}: *</Col>
                  : null
                  }
                  { configInfo.uiConfig.visibleFields.USER_language ?
                  <Col md={3}>{t("I18N_Lng_pref")}: *</Col>
                  : null
                  }
                  { isSysAdminUser ?
                    <Col>{t("I18N_Account_type")}: *</Col>
                  :
                    <Col md={3}></Col>
                  }
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  { configInfo.uiConfig.visibleFields.USER_status ?
                  <Col md={6}>
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={t("common:I18N_" + newUserStatus.value)}
                      onSelect={onUserStatusSelect}>
                      {statusList.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                            {t("common:I18N_" + item.value)}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </Col>
                  : null
                  }
                  { configInfo.uiConfig.visibleFields.USER_language ?
                  <Col md={3}>
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={t("I18N_Lng_" + newLng.value)}
                      onSelect={onLngSelect}>
                      {lngList.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                            {t("I18N_Lng_" + item.value)}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </Col>
                  : null
                  }
                  { isSysAdminUser ?
                    <Col>
                      <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                        title={newAccountType.value}
                        onSelect={onAccountTypeSelect}>
                        {accountTypeList.map((item, index) => {
                          return (
                            <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                              {item.value}
                            </Dropdown.Item>
                          );
                        })}
                      </DropdownButton>
                    </Col>
                  :
                    <Col md={3}></Col>
                  }
                </Row>

                { configInfo.uiConfig.visibleFields.USER_activeTimeWindow ?
                  <>
                    <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                      <Col md={3}>{t("I18N_User_active_start")}:</Col>
                      <Col md={3}>{t("I18N_User_active_expire")}:</Col>
                      <Col md={3}></Col>
                      <Col md={3}></Col>
                    </Row>
                    <Row className={classes.admin_pages__field_value}>
                      <Col md={3}>
                        <DateTimePicker onChange={onDatepickerChangeStart} value={activeStartTime}
/*
                           format={ (activeStartTime != null) ? "y-MM-dd h:00 a" : "y-MM-dd h:-- a" }
*/
                           format={ (activeStartTime != null) ? "y-MM-dd h a" : "y-MM-dd h a" }
                           minDate={new Date(3600000 * ( Math.round(Date.now() / 3600000)))}
                           disableClock="true"
                           clearIcon=<BiXCircle size={16} style={{color:"gray"}}/>
                           calendarIcon=<BiCalendar size={16} style={{color:"gray"}}/>
                           /* Note: zh-CN has a bug: AM/PM is shown in English, so using zh-HK for now */
                           locale={i18n.language === 'zh_HK' ? "zh-HK" : (i18n.language === 'zh_CN' ? "zh-HK" : "en")}
                        />
                      </Col>
                      <Col md={3}>
                        <DateTimePicker onChange={onDatepickerChangeUntil} value={activeExpireTime}
/*
                           format={ (activeExpireTime != null) ? "y-MM-dd h:00 a" : "y-MM-dd h:-- a" }
*/
                           format={ (activeExpireTime != null) ? "y-MM-dd h a" : "y-MM-dd h a" }
                           minDate={ activeStartTime? activeStartTime: new Date(3600000 * ( Math.round(Date.now() / 3600000)))}
                           disableClock="true"
                           clearIcon=<BiXCircle size={16} style={{color:"gray"}}/>
                           calendarIcon=<BiCalendar size={16} style={{color:"gray"}}/>
                           /* Note: zh-CN has a bug: AM/PM is shown in English, so using zh-HK for now */
                           locale={i18n.language === 'zh_HK' ? "zh-HK" : (i18n.language === 'zh_CN' ? "zh-HK" : "en")}
                        />
                      </Col>
                      <Col md={3}></Col>
                      <Col md={3}></Col>
                    </Row>
                  </>
                : null
                }

                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                  <span>{t("I18N_Req_fields")}</span>
                </Row>
                <Row>
                  <div display='flex' style={{marginTop:'15px'}}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleEditGeneralInfoCancel}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleEditGeneralInfoSubmit}>
                      {t("I18N_Save")}
                    </Button>
                    <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                      {newGeneralInfoErrMsg}
                    </span>
                  </div>
                </Row>
              </form>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label}>
                <Col>{t("I18N_Username")}:</Col>
                { configInfo.uiConfig.visibleFields.USER_jobTitle ? <Col>{t("I18N_Job_title")}:</Col> : null }
                { configInfo.uiConfig.visibleFields.USER_dept     ? <Col>{t("I18N_Dept_name")}:</Col> : null }
                { configInfo.uiConfig.visibleFields.USER_empId    ? <Col>{t("I18N_Emp_id")}:</Col>    : null }
{/*
                { configInfo.uiConfig.visibleFields.USER_userType ?
                <Col>{t("I18N_User_type")}: *</Col>
                : <Col></Col>
                }
*/}
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{userAttrs.username}</Col>
                { configInfo.uiConfig.visibleFields.USER_jobTitle ?
                <Col>{userAttrs.jobTitle}</Col>
                : null
                }
                { configInfo.uiConfig.visibleFields.USER_dept ?
                <Col style={{minWidth:'60px', flexWrap: 'wrap', wordWrap: 'break-word'}}>
                  { (i18n.language === 'en') ? userAttrs.deptName : (i18n.language === 'zh_HK' ? userAttrs.deptNameLang2 : userAttrs.deptNameLang3) }
                </Col>
                : null
                }
                { configInfo.uiConfig.visibleFields.USER_empId ?
                <Col>{userAttrs.empId}</Col>
                : null
                }
{/*
                { configInfo.uiConfig.visibleFields.USER_userType ?
                <Col>{t("common:I18N_"  + userAttrs.userType)}</Col>
                : <Col></Col>
                }
*/}
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                { configInfo.uiConfig.visibleFields.USER_status ?
                <Col>{t("I18N_User_status")}:</Col>
                : null
                }
                { configInfo.uiConfig.visibleFields.USER_language ?
                <Col>{t("I18N_Lng_pref")}:</Col>
                : null
                }
                <Col>{t("I18N_Time_pwd_updated")}:</Col>
                { isSysAdminUser ?
                  <Col>{t("I18N_Account_type")}:</Col>
                :
                  <Col></Col>
                }
              </Row>
              <Row className={classes.admin_pages__field_value}>
                { configInfo.uiConfig.visibleFields.USER_status ?
                <Col>{t("I18N_" + userAttrs.status)}</Col>
                : null
                }
                { configInfo.uiConfig.visibleFields.USER_language ?
                <Col>{t("I18N_Lng_" + userAttrs.language)}</Col>
                : null
                }
                <Col>{formatDate(userAttrs.timePwdUpdated)}</Col>
                { isSysAdminUser ?
                  <Col>{userAttrs.accountType}</Col>
                :
                  <Col></Col>
                }
              </Row>

              { configInfo.uiConfig.visibleFields.USER_activeTimeWindow && userAttrs.activeStartTime && userAttrs.activeExpireTime ?
                <>
                  <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                    <Col>{t("I18N_User_active_start")}:</Col>
                    <Col>{t("I18N_User_active_expire")}:</Col>
                    <Col></Col>
                    <Col></Col>
                  </Row>
                  <Row className={classes.admin_pages__field_value}>
                    <Col>{userAttrs.activeStartTime  ? formatDate(userAttrs.activeStartTime)  : "N/A"}</Col>
                    <Col>{userAttrs.activeExpireTime ? formatDate(userAttrs.activeExpireTime) : "N/A"}</Col>
                    <Col></Col>
                    <Col></Col>
                  </Row>
                </>
              : null}

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Created_by")}:</Col>
                <Col>{t("I18N_Time_created")}:</Col>
                <Col>{t("I18N_Updated_by")}:</Col>
                <Col>{t("I18N_Time_updated")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col style={{overflowWrap:'anywhere'}}>{userAttrs.creatorUsername}</Col>
                <Col>{formatDate(userAttrs.timeCreated)}</Col>
                <Col style={{overflowWrap:'anywhere'}}>{userAttrs.updaterUsername}</Col>
                <Col>{formatDate(userAttrs.timeUpdated)}</Col>
              </Row>
            </Container>
          }
        </div>

        { configInfo.uiConfig.visibleFields.USER_surname || configInfo.uiConfig.visibleFields.USER_givenName || configInfo.uiConfig.visibleFields.USER_middleName ?
        <div className={classes.admin_pages__page_subtitle} style={{ marginTop: '20px' }}>
          <div display='flex'>
            <span>
              {t("I18N_Name_details")}
            </span>
            {editModeName || (!configInfo.appConfig.isPrimaryIdentProvider && userAttrs.accountType === 'USER') ?
              null
              :
              <span style={{ marginLeft: '15px', fontSize: '9px', color: 'SteelBlue', cursor: 'pointer' }} onClick={handleEditName}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
            }
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editModeName ?
            <Container>
              <Form style={{ borderColor: 'lightgray' }}>
                <Row className={classes.admin_pages__field_label} style={{ marginTop: '15px' }}>
                  <Col>{t("I18N_Surname")}:</Col>
                  <Col>{t("I18N_Given_name")}:</Col>
                  <Col>{t("I18N_Middle_name")}:</Col>
                  { configInfo.uiConfig.visibleFields.USER_fullNameLang2 ? <Col>{t("custom:I18N_Full_name_lang2")}:</Col> : <Col /> }
                </Row>
                <Row >
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="surname" value={surname}
                      onChange={(e) => onNameInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="givenName" value={givenName}
                      onChange={(e) => onNameInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="middleName" value={middleName}
                      onChange={(e) => onNameInputChange(e)} />
                  </Col>
                  { configInfo.uiConfig.visibleFields.USER_fullNameLang2 ?
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="fullNameLang2" value={fullNameLang2}
                      onChange={(e) => onNameInputChange(e)} />
                  </Col>
                  : <Col />
                  }
                </Row>
                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                  <span>{t("I18N_Req_fields")}</span>
                </Row>
                <Row>
                  <div display='flex' style={{marginTop:'15px'}}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleEditNameCancel}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleEditNameSubmit}>
                      {t("I18N_Save")}
                    </Button>
                    <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                      {newNameErrMsg}
                    </span>
                  </div>
                </Row>
              </Form>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label} style={{ marginTop: '15px' }}>
                <Col>{t("I18N_Surname")}:</Col>
                <Col>{t("I18N_Given_name")}:</Col>
                <Col>{t("I18N_Middle_name")}:</Col>
                { configInfo.uiConfig.visibleFields.USER_fullNameLang2 ? <Col>{t("custom:I18N_Full_name_lang2")}:</Col> : <Col /> }
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{userAttrs.surname}</Col>
                <Col>{userAttrs.givenName}</Col>
                <Col>{userAttrs.middleName}</Col>
                { configInfo.uiConfig.visibleFields.USER_fullNameLang2 ? <Col>{userAttrs.fullNameLang2}</Col> : <Col /> }
              </Row>
            </Container>
          }
        </div>
        : null
        }

        { configInfo.uiConfig.visibleFields.USER_email || configInfo.uiConfig.visibleFields.USER_mobilePhone || configInfo.uiConfig.visibleFields.USER_workPhone ?
        <div className={classes.admin_pages__page_subtitle} style={{ marginTop: '20px' }}>
          <div display='flex'>
            <span>
              {t("I18N_Contact_info")}
            </span>
            {editModeContactInfo || (!configInfo.appConfig.isPrimaryIdentProvider && userAttrs.accountType === 'USER') ?
              null
              :
              <span style={{ marginLeft: '15px', fontSize: '9px', color: 'SteelBlue', cursor: 'pointer' }} onClick={handleEditContactInfo}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
            }
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editModeContactInfo ?
            <Container>
              <Form>
                <Row className={classes.admin_pages__field_label}>
                  <Col>{t("I18N_Email")}: *</Col>
                  <Col>{t("I18N_Mobile_phone")}:</Col>
                  <Col>{t("I18N_Work_phone")}:</Col>
                  <Col></Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="email" value={email}
                      onChange={(e) => onContactInfoInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="mobilePhone" value={mobilePhone}
                      onChange={(e) => onContactInfoInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="workPhone" value={workPhone}
                      onChange={(e) => onContactInfoInputChange(e)} />
                  </Col>
                  <Col></Col>
                </Row>
                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                  <span>{t("I18N_Req_fields")}</span>
                </Row>
                <Row>
                  <Col md={2}>
                    <div display='flex' style={{marginTop:'15px'}}>
                      <Button size="tiny" variant="outline-secondary" className="mb-3"
                        style={{ float: 'left' }}
                        onClick={handleEditContactInfoCancel}>
                        {t("I18N_Cancel")}
                      </Button>
                      <Button size="tiny" variant="outline-secondary" className="mb-3"
                        style={{ marginLeft: '15px' }}
                        onClick={handleEditContactInfoSubmit}>
                        {t("I18N_Save")}
                      </Button>
                    </div>
                  </Col>
                  <Col md={10} style={{marginTop:'15px'}}>
                    <span className={classes.admin_pages__field_label} style={{color:'rgba(200,0,0)', verticalAlign:'top'}}>
                      {newContactInfoErrMsg}
                    </span>
                  </Col>
                </Row>
              </Form>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label}>
                <Col>{t("I18N_Email")}:</Col>
                <Col>{t("I18N_Mobile_phone")}:</Col>
                <Col>{t("I18N_Work_phone")}:</Col>
                <Col></Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{userAttrs.email}</Col>
                <Col>{userAttrs.mobilePhone}</Col>
                <Col>{userAttrs.workPhone}</Col>
                <Col></Col>
              </Row>
            </Container>
          }
        </div>
        : null
        }

        { configInfo.appConfig.isPrimaryIdentProvider && !configInfo.appConfig.isSimpleUserCreation ?
          <div className={classes.admin_pages__page_subtitle} style={{ marginTop: '20px' }}>
            <div display='flex'>
              <span>
                {t("I18N_Manage_auth")}
              </span>
            </div>

            <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

            <Container>

              { (hasAnyPerm(user, ["UserAdminResetPasswordAccess"], true))
                && userAttrs && userAttrs.status &&
                (   (userAttrs.status === 'USR_ST_ACTIVE')
                 || (userAttrs.status === 'USR_ST_FORGOT_PWD_PEND_EMAIL_V')
                 || (userAttrs.status === 'USR_ST_READY_TO_SET_PWD')
                 || (userAttrs.status === 'USR_ST_ADMIN_PWD_PEND_EMAIL_V')
                 || (userAttrs.status === 'USR_ST_READY_TO_SET_SEC_N_PWD')) ?
                <div display='flex' style={{marginTop:'20px'}}>
                  <span>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={resetUserPwd}>
                      {t("I18N_Reset_pwd_sec_q")}
                    </Button>
                  </span>
                  <span className={classes.admin_pages__field_label} style={{ marginLeft: '30px', color: 'rgba(200,0,0)' }}>
                    {pwdResetErrMsg}
                  </span>
                  <span className={classes.admin_pages__field_label} style={{ marginLeft: '30px' }}>
                    {pwdResetMsg}
                  </span>
                </div>
              :
                null
              }

              { configInfo.uiConfig.visibleFields.USER_mfa ?
                <div>
                  <div display='flex' style={{marginTop:'20px'}}>
                    <span className={classes.admin_pages__field_label}>
                      {t("I18N_MFA")}
                    </span>
                    {editModeMFA || !configInfo.appConfig.isPrimaryIdentProvider ?
                      null
                      :
                      <span style={{ marginLeft: '15px', fontSize: '9px', color: 'SteelBlue', cursor: 'pointer' }} onClick={handleEditMFA}>
                        <PencilIcon size={10} />
                        {t("I18N_Edit")}
                      </span>
                    }
                  </div>

                  <Form className={classes.admin_pages__field_value} style={{ margin: '10px 0 0 10px' }}>
                    <Form.Check
                      disabled={!editModeMFA}
                      type="switch"
                      id="mfa-email"
                      label={t("I18N_MFA_via_email")}
                      checked={newMfaWithEmail}
                      onChange={() => {
                        setNewMfaWithEmail(!newMfaWithEmail)
                      }}
                    />
{/*
                    <Form.Check
                      disabled={!editModeMFA}
                      type="switch"
                      id="mfa-sms"
                      label={t("I18N_MFA_via_sms")}
                      checked={newMfaWithSMS}
                      onChange={() => {
                        setNewMfaWithSMS(!newMfaWithSMS)
                      }}
                    />
*/}
                  </Form>

                  {editModeMFA ?
                    <Row>
                      <div display='flex' style={{ marginTop: '15px' }}>
                        <Button size="tiny" variant="outline-secondary" className="mb-3"
                          style={{ float: 'left' }}
                          onClick={handleEditMFACancel}>
                          {t("I18N_Cancel")}
                        </Button>
                        <Button size="tiny" variant="outline-secondary" className="mb-3"
                          style={{ marginLeft: '15px' }}
                          onClick={handleEditMFASubmit}>
                          {t("I18N_Save")}
                        </Button>
                        <span className={classes.admin_pages__field_label} style={{ marginLeft: '30px', color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                          {newMfaErrMsg}
                        </span>
                      </div>
                    </Row>
                    :
                    null
                  }

                </div>
              : null
              }
            </Container>
          </div>
        :
          null
        }

        {    configInfo.appConfig
          && configInfo.appConfig.isSimpleUserCreation                // Only for simple user creation mode
          && (   configInfo.appConfig.isPrimaryIdentProvider          // Primary identity provide mode
              || (   !configInfo.appConfig.isPrimaryIdentProvider     // Non-primary identity provider mode and account type NOT user. (API, etc.)
                  && (userAttrs.accountType !== 'USER')))
          ?
          <div className={classes.admin_pages__page_subtitle} style={{ marginTop: '20px' }}>
            <div display='flex'>
              <span>
                {t("I18N_Reset_password")}
              </span>

              {!editModePwdInfo ?
                <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditPwdInfo}>
                  <PencilIcon size={12} />
                  {t("I18N_Edit")}
                </span>
                :
                null
              }
            </div>

            <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

            { editModePwdInfo ?
            <Container>
              <Form>
                <Row className={classes.admin_pages__field_label}>
                  <Col>{t("I18N_Password")}: *</Col>
                  <Col>{t("I18N_Confirm_password")}: *</Col>
                  <Col></Col>
                  <Col></Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="password" value={password}
                      onChange={(e) => onPwdInfoInputChange(e)} />
                    { platformConfig && platformConfig.config ?
                        <OverlayTrigger overlay={<Tooltip id="tooltip-pwd-complexity" style={{fontSize:'11px'}}>
                                                   { i18n.language === 'en'    ? decodeURIComponent(platformConfig.config.public.pwdComplexity.hint.en)    :
                                                     i18n.language === 'zh_HK' ? decodeURIComponent(platformConfig.config.public.pwdComplexity.hint.zh_HK) :
                                                     i18n.language === 'zh_CN' ? decodeURIComponent(platformConfig.config.public.pwdComplexity.hint.zh_CN) :
                                                     decodeURIComponent(platformConfig.config.public.pwdComplexity.hint.en) }
                                                 </Tooltip>}>
                          <span style={{marginLeft:'5px', cursor:'pointer'}}>
                            <BiHelpCircle size={15} />
                          </span>
                        </OverlayTrigger>
                      : null
                    }
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="confirmPassword" value={confirmPassword}
                      onChange={(e) => onPwdInfoInputChange(e)} />
                  </Col>
                  <Col></Col>
                  <Col></Col>
                </Row>
                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                  <span>{t("I18N_Req_fields")}</span>
                </Row>
                <Row style={{marginTop:'15px'}}>
                  <Col md={2}>
                    <div display='flex'>
                      <Button size="tiny" variant="outline-secondary" className="mb-3"
                        style={{ float: 'left' }}
                        onClick={handleEditPwdInfoCancel}>
                        {t("I18N_Cancel")}
                      </Button>
                      <Button size="tiny" variant="outline-secondary" className="mb-3"
                        style={{ marginLeft: '15px' }}
                        onClick={handleEditPwdInfoSubmit}>
                        {t("I18N_Save")}
                      </Button>
                    </div>
                  </Col>
                  <Col md={10}>
                    <span className={classes.admin_pages__field_label} style={{color:'rgba(200,0,0)'}}>
                      {newPwdInfoErrMsg}
                    </span>
                  </Col>
                </Row>
              </Form>
            </Container>
            : null
            }
          </div>
        :
          null
        }

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'30px'}}>
          <div style={{display:'flex'}}>
            <span>
              {t("I18N_Ugroups_of_user")}
            </span>
          </div>

          <hr className="solid" style={{ margin: '0 0 0 0' }}></hr>

          <div style={{overflow:'auto', fontSize:'11px', marginTop:'10px'}}>

            <Table hover responsive size="sm">
              <thead style={{color:'#686868', fontSize:'12px', borderBottom:"2px solid #D0D0D0"}}>
                <tr>
                  <th style={{fontWeight:'400'}}>{t("I18N_Name")}</th>
                  <th style={{fontWeight:'400'}}>{t("I18N_UGroup_name") + " (" + t("custom:I18N_Language_2") + ")"}</th>
                  <th style={{fontWeight:'400'}}>{t("I18N_UGroup_name") + " (" + t("custom:I18N_Language_3") + ")"}</th>
                  <th style={{fontWeight:'400'}}>{t("I18N_Time_joined")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(ugroupList && ugroupList.length > 0) ? ugroupList.map(renderUgroup) : null}
              </tbody>
            </Table>
          </div>
        </div>


        {/* <div className={classes.admin_pages__page_subtitle} style={{marginTop:'30px'}}>
          <div style={{display:'flex'}}>
            <span>
              {t("I18N_Roles_assigned_to_user")}
            </span>

            { configInfo.appConfig.isPrimaryIdentProvider ?
              <span style={{marginLeft:'auto', marginRight:'20px'}}>
                <OverlayTrigger overlay={<Tooltip id="tooltip-new_role" style={{fontSize:'11px'}}>{t("I18N_TIP_Add_new_role")}</Tooltip>}>
                  <span onClick={handleAddRoles} style={{ marginLeft: '0px', marginRight: '0px', marginTop: '3px', cursor: 'pointer', color: 'rgb(100,100,100)' }}>
                    <BiPlus size={18} />
                  </span>
                </OverlayTrigger>
              </span>
            :
              null
            }
          </div>

          <hr className="solid" style={{ margin: '0 0 0 0' }}></hr>

          <div style={{overflow:'auto', fontSize:'11px', marginTop:'10px'}}>

            <Table hover responsive size="sm">
              <thead style={{color:'#686868', fontSize:'12px', borderBottom:"2px solid #D0D0D0"}}>
                <tr>
                  <th style={{fontWeight:'400'}}>{t("I18N_Role_id")}</th>
                  <th style={{fontWeight:'400'}}>{t("I18N_Role_name")}</th>
                  <th style={{fontWeight:'400'}}>{t("I18N_Description")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(roleList && roleList.length > 0) ? roleList.map(renderRole) : null}
              </tbody>
            </Table>
          </div>
        </div> */}

        <hr className="solid" style={{ marginTop: '30px' }}></hr>

        <div style={{ margin:'0', padding:'0', display: 'flex' }}>
          <Form.Group>
            <Button size="sm" variant="outline-secondary" className="mb-1"
              style={{ marginLeft:'0px', marginTop:'3px', fontSize:'12px', fontWeight: '300' }}
              onClick={handleClose}>
              {t("I18N_Close")}
            </Button>
            { userAttrs && userAttrs.status === 'USR_ST_PEND_ACTV' ?
              <Button size="sm" variant="outline-secondary" className="mb-1"
                style={{ marginLeft:'10px', marginTop:'3px', fontSize:'12px', fontWeight: '300' }}
                onClick={handleReissueActv}>
                {t("I18N_Reissue_user_actv")}
              </Button>
              : null
            }
          </Form.Group>
          <span style={{marginLeft:'20px', marginTop:'8px', color:'rgba(200,0,0)', fontSize:'12px'}}>
             {errMsg}
          </span>
        </div>

        <Modal show={showRoleInfo} onHide={handleRoleInfoClose} centered >
          <Modal.Body>
            <div className={classes.admin_pages__page_subtitle} style={{marginTop:'0px'}}>
              <div display='flex'>
                <span>
                  {t("I18N_Role_info")}
                </span>
              </div>

              <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

              <Container>
                <Row className={classes.admin_pages__field_label}>
                  <Col>{t("I18N_Role_name")}:</Col>
                  <Col>{t("I18N_Description")}:</Col>
                </Row>
                  {roleSelected ?
                    <>
                      <Row className={classes.admin_pages__field_value}>
                        <Col>{t("custom:I18N_" + roleSelected.nameCode)}</Col>
                        <Col>
                          <textarea readOnly className={classes.admin_pages__field_editable} style={{resize:'none', borderStyle:'none', width:'100%'}}
                                    name="description" value={roleSelected.description} />
                        </Col>
                      </Row>
                    </>
                  :
                    <></>
                  }
              </Container>


              <div display='flex'>
                <span>
                  {t("I18N_Permissions")}
                </span>
              </div>

              <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>
            </div>

            <div style={{overflow:'auto', fontSize:'11px', marginTop:'10px'}}>
              <Table hover responsive size="sm">
                <thead style={{color:'#686868', fontSize:'12px', borderBottom:"2px solid #D0D0D0"}}>
                  <tr>
                    <th style={{fontWeight:'400'}}>{t("I18N_Perm_name")}</th>
                    <th style={{fontWeight:'400'}}>{t("I18N_Description")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rolePermList.map(renderRolePerm)}
                </tbody>
              </Table>
            </div>

            <div>
              <Button size="sm" variant="outline-secondary"
                style={{ marginBottom:'5px', fontSize: '12px', fontWeight: '300' }}
                onClick={handleRoleInfoClose}>
                {t("I18N_Close")}
              </Button>
            </div>
          </Modal.Body>
        </Modal>

{/* ***** The spinner makes the role modal dialog's background color becomes transparent.  Don't know why. ***** */}
        <Modal show={showAddRoleDialog} onHide={handleAddRolesClose} centered >
          <Modal.Body>

            <div className={classes.admin_pages__page_subtitle} style={{marginTop:'0px'}}>
              <div display='flex'>
                <span>
                  {t("I18N_Roles")}
                </span>
              </div>

              <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>
            </div>

            <div style={{overflow:'auto', fontSize:'11px', marginTop:'10px'}}>
              <Table hover responsive size="sm">
                <thead style={{color:'#686868', fontSize:'12px', borderBottom:"2px solid #D0D0D0"}}>
                  <tr>
                    <th></th>
                    <th style={{fontWeight:'400'}}>{t("I18N_Role_name")}</th>
                    <th style={{fontWeight:'400'}}>{t("I18N_Description")}</th>
                  </tr>
                </thead>
                <tbody>
                  {unassignedRoleList? unassignedRoleList.map(renderAddRoles) : null}
                </tbody>
              </Table>
            </div>

            <div display='flex' style={{ marginTop: '15px' }}>
              <Button size="tiny" variant="outline-secondary" className="mb-3"
                style={{ float: 'left' }}
                onClick={handleAddRolesClose}>
                {t("I18N_Cancel")}
              </Button>
              <Button size="tiny" variant="outline-secondary" className="mb-3"
                style={{ marginLeft: '15px' }}
                onClick={handleAddRolesSubmit}>
                {t("I18N_Assign_roles")}
              </Button>
              <span className={classes.admin_pages__field_label} style={{ marginLeft: '30px', color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                {addRoleErrMsg}
              </span>
            </div>
          </Modal.Body>
        </Modal>
{/* ***** The spinner makes the role modal dialog's background color becomes transparent.  Don't know why. *****
*/}
        <LoadingSpinner loading={loading} />
{/* ***** The spinner makes the role modal dialog's background color becomes transparent.  Don't know why. *****
*/}

      </Card>
    </>
  );
}

export default UserAdminDetailsView;
